import { type ReactNode } from 'react';
import { Drawer } from 'vaul';
import { Button } from './Button';

interface DialogProps {
  open: boolean;
  title: string;
  description?: string;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onCancel: () => void;
  confirmDisabled?: boolean;
  confirmTone?: 'primary' | 'danger';
}

// 모바일 바텀시트 (vaul) — 기존 센터 모달 API를 유지한 채 내부만 교체.
// 드래그 핸들 + 아래로 스와이프 닫기, 포커스 트랩/ESC/스크롤 잠금은 vaul(Radix)이 처리.
export function Dialog({
  open,
  title,
  description,
  children,
  confirmLabel = '확인',
  cancelLabel = '취소',
  onConfirm,
  onCancel,
  confirmDisabled,
  confirmTone = 'primary',
}: DialogProps) {
  return (
    <Drawer.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]" />
        <Drawer.Content
          aria-label={title}
          aria-modal="true"
          aria-describedby={description ? undefined : ''}
          className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[88dvh] w-full max-w-screen-sm flex-col rounded-t-[28px] bg-surface pt-3 [box-shadow:var(--shadow-sheet)] focus:outline-none"
        >
          <div aria-hidden="true" className="mx-auto mb-4 h-[4.5px] w-10 flex-none rounded-full bg-surface-border" />
          {/* 콘텐츠 — 키보드/긴 달력 등으로 넘치면 내부 스크롤 */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-2">
            <Drawer.Title className="text-feature font-semibold tracking-tight-feature text-ink">
              {title}
            </Drawer.Title>
            {description && (
              <Drawer.Description className="mt-3 text-body text-ink-muted">
                {description}
              </Drawer.Description>
            )}
            {children && <div className="mt-4 text-body text-ink">{children}</div>}
          </div>
          {/* 액션 버튼 — 항상 하단 고정 (콘텐츠 길이에 무관) */}
          <div className="flex shrink-0 gap-2 px-6 pb-[max(env(safe-area-inset-bottom),20px)] pt-3">
            <Button variant="ghost" onClick={onCancel} className="flex-1">
              {cancelLabel}
            </Button>
            {onConfirm && (
              <Button
                variant={confirmTone === 'danger' ? 'danger' : 'brand'}
                onClick={onConfirm}
                disabled={confirmDisabled}
                className="flex-1"
              >
                {confirmLabel}
              </Button>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
